import { AI_CODE_ENDPOINT, IS_EDITOR_MODE } from 'config/envVariable';
import { CUSTOM_HTML, GASHAPON, GARAPON } from 'config/promoLotteryTypes';
import { parseJSON_ifParsable } from 'utils/objectManipulate';

/**
 * AI web game 遊戲檔來源的單一收斂點：把 `promoGameTypes` 宣告的 `aiWebGame.source`
 * 軸（`'upload'` 客戶上傳 ／ `'builtin'` 內建出貨檔）解析成實際的 `gameCdnUrl`。
 *
 * 宣告在 `config/promoGameTypes.js`、解析在本檔——同名是刻意的，別在兩邊各自推導。
 * 涵蓋 promo 層（type 40，一個 promo 一份）與 lottery 層（type 10 上傳 / 11、12 內建）。
 */

/**
 * 內建 HTML 給獎動畫（lottery type 11 扭蛋機 / 12 福引機）的遊戲檔版本，逐款一個常數。
 *
 * ⚠️ 版本號是 **code 常數，不讀活動資料**：`resolveLotteryGameSource` 直接查下面這張表，
 * promo 47/49 的畫布與 client 也都走同一條（`settingGame.aiCodeVersion` 只有 41–46 在讀）。
 * 也就是說**改了這裡就是全體切換**，沒有「舊活動留在舊版」這回事——
 *  - 平常內建檔更新一律**原地覆蓋同版號**，不動這裡；
 *  - 升版是為了繞開 CDN 對同名檔的快取（#86eyjuajh）。
 *    升版後所有既有活動下一次載入就吃新檔，所以**改了 gameConfig 形狀時要問「舊設定進新檔
 *    會怎樣」**，答案必須是可接受的降級（新檔讀不到的舊 key 一律當沒設定）。
 *  - 前端 deploy 前新版兩個檔（含 -obfuscated）必須先上 S3，否則整批活動的 iframe 404。
 *  - ⚠️ **repo 內只留當前版號那一份**，舊版檔一律隨升版刪掉（#86eykr8w9 收掉 47/49 的 v1.0）。
 *    所以 rollback **不是**把這個常數往回改就好——舊檔已不在 repo 也不保證還在 S3，
 *    得先 `git revert` 把該版 HTML 取回、重跑 `yarn game:build`、重新上傳 S3，才能改回常數。
 *
 * S3 部署路徑對齊後端票 86eyejdh8。
 *
 * ⚠️ **八款內建遊戲目前一律停在 `v1.0`**（#86eymgunz）。41–46 的版號釘在每個活動裡，
 * 升版會把既有活動留在舊檔，所以那六款的修正只能原地覆蓋 v1.0 出貨；47/49 雖然沒有這個
 * 限制，但一起對齊 v1.0 才不會出現「同一批出貨檔各自不同版號」的混亂。
 * 之後真的需要繞快取時，**八款一起 bump**。
 *
 * 以下兩次改動都以**原地覆蓋 v1.0** 出貨，沒有各自升版：
 *
 * 福引機（#86eyjy9mg）：彩球從三顆固定色改成「12 色票 × 獎品關鍵詞」。舊活動存的
 * `ball.colorA/B/C` 在新檔沒有對應 key → 落球顏色變成全色盤隨機（pre-release 決議接受）。
 *
 * 福引機（#86eymx101）：彩球再從「12 色票 × 關鍵詞」反轉成「逐獎品指定顏色」
 * （`ball.prizeColors`，鍵＝prizeUUID）。舊活動存的 `ball.kogane`／`shirogane`… 在新檔
 * 沒有對應 key → 落球顏色改走「依獎品順序套預設色票」。比上一次的降級好（不再是隨機），
 * 且 prod 當時沒有任何 type 12 活動，故無實際影響。
 *
 * 扭蛋機（#86eykr8w9）：section key 對齊福引機（`hero`→`mainVisual`、`reveal`→
 * `prizeModal`），刻意不寫相容映射。舊活動存在 `AnimationSettings.gameConfig` 的 `hero`／
 * `reveal` 覆寫在新檔掛不回去 → 主視覺與中獎視窗回到內建預設（prod 尚無 promo 47/49 的
 * 任何檔案，dev 測試活動重設即可）。這種「改 gameConfig 形狀」的改動正是上面要求先問降級的情境。
 */
export const GASHAPON_GAME_VERSION = 'v1.0';
export const GARAPON_GAME_VERSION = 'v1.0';

// 沒有 fallback 是刻意的：漏設一款會拿到 undefined 的 URL 而當場現形，
// 比靜默套上另一款的版號、指到不存在的檔好查
const builtinLotteryGameVersions = {
    [GASHAPON]: GASHAPON_GAME_VERSION,
    [GARAPON]: GARAPON_GAME_VERSION
};

export function getBuiltinLotteryGameVersion(lotteryType) {
    return builtinLotteryGameVersions[Number(lotteryType)];
}

const builtinLotteryGameCdnUrls = {
    [GASHAPON]: `${AI_CODE_ENDPOINT}/gashapon/Gashapon.html`,
    [GARAPON]: `${AI_CODE_ENDPOINT}/garapon/Garapon.html`
};

/**
 * 內建 HTML 給獎動畫型別（11 扭蛋機 / 12 福引機）。編輯器據此分流：
 * 內建型永遠有 URL、無上傳鏈（畫布/面板不顯示上傳與重新上傳入口）。
 */
export function isBuiltinLotteryGameType(lotteryType) {
    const parsed = Number(lotteryType);
    return parsed === GASHAPON || parsed === GARAPON;
}

/**
 * 已實作 `feverGame.onGameReady()` 首屏就緒訊號的獎池型別（#86eyjp7qb）。
 *
 * 平台端的 GameIframe 只有對名單內的遊戲才會「等訊號才解除隱藏」；名單外一律維持
 * 舊行為（載入完就顯示），所以新增一款遊戲的成本就是在這裡多加一個型別。
 * 反過來說也別先加：加了卻沒送訊號，玩家會白等到 GAME_READY_TIMEOUT_MS 才看到畫面。
 *
 * 客戶上傳型（10 / 40）永遠不在名單內——那是客戶的 HTML，我們無從保證它有實作。
 */
const GAME_READY_CAPABLE_LOTTERY_TYPES = [GASHAPON, GARAPON];

export function supportsGameReadySignal(lotteryType) {
    return GAME_READY_CAPABLE_LOTTERY_TYPES.includes(Number(lotteryType));
}

/**
 * 內建遊戲檔的 cache-bust 常數（#86eykvm7g）。**改了內建遊戲檔就 bump 這裡。**
 *
 * 格式是 `yyyymmddhhmm`（bump 當下的本地時間，`date +%Y%m%d%H%M`）。精確到分鐘是因為
 * 同一天常會改好幾輪，只寫日期的話第二輪 bump 不出新值，等於沒 bump。
 * 完整流程見 `docs/agents/ai-game.md`。
 *
 * 為什麼需要它：41–46 的版本號不在 code、而是**每個活動一份**（建活動時寫死
 * `DEFAULT_AI_CODE_VERSION`），所以那條路沒有「升版」這道門 —— 內建檔只能原地覆蓋
 * 同名的 `-v1.0.html`，而檔案帶一年 immutable 快取，改完的檔案送不到已經玩過的玩家
 * （也送不到開過編輯器的管理員）手上。手動清 Cloudflare 也救不到玩家自己的瀏覽器快取。
 * 47/49 有 `GASHAPON_GAME_VERSION` / `GARAPON_GAME_VERSION` 可以 bump，但一併走這條
 * 以免兩套心智模型。
 *
 * ⚠️ **所有環境都生效**（dev 與 prod 都掛 query）。所以：
 *  - 不需要動後端 SSRF 白名單（那條只驗客戶上傳的 URL，內建檔本來就不經後端）；
 *  - 改檔後 bump 這裡就能換掉 URL；沒 bump 的話仍照 #86eyjuajh 的規範走人工
 *    **Prefix purge**（S3 對 CORS 請求回 `Vary: Origin`，同一 URL 在 Cloudflare 存兩份，
 *    單一 URL purge 只清得到不帶 `Origin` 的那份，而平台載遊戲一律走跨源 `fetch()`）。
 */
export const BUILTIN_GAME_CACHE_BUST = '202610191930';

/**
 * 把 cache-bust query 掛到內建遊戲檔 URL 尾巴。
 *
 * 空字串照原樣回傳是硬性要求：呼叫端會拿 `getGameCdnUrl(type)` 的 truthiness 判斷
 * 「這個型別有沒有內建遊戲檔」，回一個裸 `?r=...` 會讓每個沒有內建檔的型別都變 truthy。
 * （版型是否全出血已改由 `isAiWebGame` 判斷，不再依賴這個 URL 是否存在。）
 *
 * 刻意不 export：唯一的入口是 `applyAiCodeVersionSuffix`，多一個公開入口就等於多一條
 * 「有些內建 URL 沒掛到」的路。`&` 分支是防呆——今天沒有帶 query 的內建 endpoint，
 * 但真的出現時掛出第二個 `?` 會是靜默壞掉。
 */
function applyBuiltinGameCacheBust(gameCdnUrl) {
    if (!gameCdnUrl) return '';
    const separator = gameCdnUrl.includes('?') ? '&' : '?';
    return `${gameCdnUrl}${separator}r=${BUILTIN_GAME_CACHE_BUST}`;
}

// 與 41–46 promo 遊戲共用的版本/混淆後綴規則（正式環境固定用混淆版）。
// 產出的 URL 尾巴會多一段 cache-bust query（見 BUILTIN_GAME_CACHE_BUST）——
// 這是刻意收斂在此：內建遊戲 URL 只有這一個出口，掛在這裡才不會漏掉任何一款。
export function applyAiCodeVersionSuffix(
    gameCdnUrl,
    aiCodeVersion,
    useObfuscated = !IS_EDITOR_MODE
) {
    if (!gameCdnUrl) return '';
    const suffix = useObfuscated ? `-${aiCodeVersion}-obfuscated.html` : `-${aiCodeVersion}.html`;
    // 先補版本後綴、再掛 cache-bust，讓 query 落在整段 URL 的最後一段（單純為了
    // 好讀與好比對；`.replace('.html', …)` 只換第一個 '.html'，兩種順序都會產出
    // 同一個字串，所以這不是正確性上的限制）
    return applyBuiltinGameCacheBust(gameCdnUrl.replace('.html', suffix));
}

const warnedLotteryKeys = new Set();
function warnMissingGameCdnUrl(lottery) {
    const dedupeKey = lottery?.uuid ?? lottery?.type;
    if (warnedLotteryKeys.has(dedupeKey)) return;
    warnedLotteryKeys.add(dedupeKey);
    console.warn(
        `[aiWebGameSource] lottery type ${lottery?.type} 缺 gameCdnUrl，揭曉動畫無法載入`,
        lottery?.uuid
    );
    // sentry.js 的 import 鏈會拉進 client hooks / redux，本檔是被 promoGameTypes
    // 引用的基礎 config，靜態 import 會有循環初始化風險，故告警時才動態載入
    import('sentry').then(({ sentryCaptureException }) =>
        sentryCaptureException({
            errType: 'LOTTERY_GAME_SOURCE_MISSING_CDN_URL',
            extraInfo: { lotteryType: lottery?.type, lotteryUuid: lottery?.uuid }
        })
    );
}

/**
 * 讀 per-lottery 的上傳紀錄 `AnimationSettings.aiWebGameFile`
 * （`{ gameCdnUrl, uploadedAt, fileName, verifyReport }`）。
 *
 * key 叫 `aiWebGameFile` 而非 `aiWebGame`：後者在本專案已被 `promoGameTypes` 的
 * 靜態宣告軸（`aiWebGame: { play, source }`）佔用，同名會讓「型別宣告」與
 * 「上傳紀錄」兩件事混在一起。`File` 也剛好說明了這包只在上傳型（10 / 40）存在，
 * 內建型（11 / 12）沒有。寫入端配對見 `utils/aiGameVerify/aiWebGameFileRecord.js`。
 *
 * ⚠️ 這四個欄位**必須巢狀在一個物件底下**，不可平鋪回 AnimationSettings 第一層：
 * 第一層的約定是「值必須是 JSON」——存檔走 `objFirstLevelJSONStringify`（只 stringify
 * 物件）、讀取走 `parseAnimationSettings` 對每個值 `JSON.parse`，而**失敗時回 `{}` 而非原值**
 * （`utils/parseAnimationSettings.js:32-34`）。所以平鋪的裸字串（URL／ISO 時間／檔名）
 * 過完編輯器 Prize 側欄的 parse 鏈會全部變成 `{}`，造成
 * 「Objects are not valid as a React child (found: object with keys {})」。
 * 包成物件後整包 round-trip 乾淨，內層字串不被第一層的 parse 碰到。
 *
 * 三個字串欄位在此保證型別：非字串一律回 ''，讓 caller 可以直接 render
 * （這就是原本 crash 的那一行 `{fileName || '-'}` —— 收斂在 reader 才不會每個
 * 消費點各自防一次、漏一個就再炸一次）。
 *
 * @param {object} animationSettings - lottery.AnimationSettings（可能是原始 JSON 字串形態或已 parse 過）
 * @returns {{ gameCdnUrl: string, uploadedAt: string, fileName: string, verifyReport: object|undefined }}
 */
export function readLotteryAiWebGameFile(animationSettings) {
    return normalizeAiWebGameFileRecord(animationSettings?.aiWebGameFile);
}

/**
 * 讀 promo 層的上傳紀錄 `settingGame.aiWebGameFile`（type 40 自訂挑戰：計分型、
 * 客戶上傳 HTML，一個 promo 一份，與 per-lottery 的紀錄完全同形
 * `{ gameCdnUrl, uploadedAt, fileName, verifyReport }`，見 #86eyepx3a 決議④）。
 * settingGame 第一層同樣走 objFirstLevelJSONStringify/Parse，巢狀約定同上。
 * 上傳檔 immutable、不加版本後綴（同 type 10）。
 */
export function readPromoAiWebGameFile(settingGame) {
    return normalizeAiWebGameFileRecord(settingGame?.aiWebGameFile);
}

function normalizeAiWebGameFileRecord(rawRecord) {
    const parsed = parseJSON_ifParsable(rawRecord);
    const record = parsed && typeof parsed === 'object' ? parsed : {};
    const asText = value => (typeof value === 'string' ? value : '');
    return {
        ...record,
        gameCdnUrl: asText(record.gameCdnUrl),
        uploadedAt: asText(record.uploadedAt),
        fileName: asText(record.fileName)
    };
}

/**
 * 依 lottery type 解析給獎遊戲的 HTML 位置與 config（單一收斂點，禁止在
 * 揭曉頁/編輯器各自從 promo.type 或 settingGameStyle 推導 —— 一般模組的
 * 獎池選 11/12 時 promo-scoped 推導會靜默降級成「有獎但永遠沒動畫」）。
 *
 * @param {{ type: number|string, uuid?: string, AnimationSettings?: object }} lottery
 * @param {{ warnOnMissing?: boolean }} [options] - warnOnMissing=false 供「缺 URL 是正常態」的
 *   caller 使用（如編輯器的未上傳空狀態），避免把 pre-upload 打進 Sentry
 * @returns {{ gameCdnUrl: string, gameConfig: object }}
 *   - type 10（自訂 HTML）：讀 per-lottery AnimationSettings.aiWebGameFile.gameCdnUrl（上傳檔，immutable、不加版本後綴）
 *   - type 11/12（內建）：查內建常數表 + 版本/混淆後綴
 *   - 其他型別：空來源（不告警，caller 不該對其渲染遊戲 iframe）
 */
export function resolveLotteryGameSource(lottery, { warnOnMissing = true } = {}) {
    const emptySource = { gameCdnUrl: '', gameConfig: {} };
    if (!lottery) return emptySource;

    const type = Number(lottery.type);
    // 後端把 AnimationSettings 的值以 JSON 字串儲存（objFirstLevelJSONStringify），
    // 也可能已被上游 parse 過（如 parseAnimationSettings 的輸出、裸 URL 字串），
    // parseJSON_ifParsable 失敗回原值，兩種形態都吃
    const settings = lottery.AnimationSettings ?? {};
    const parsedGameConfig = parseJSON_ifParsable(settings.gameConfig);
    const gameConfig =
        parsedGameConfig && typeof parsedGameConfig === 'object' ? parsedGameConfig : {};

    if (type === CUSTOM_HTML) {
        const { gameCdnUrl } = readLotteryAiWebGameFile(settings);
        if (!gameCdnUrl && warnOnMissing) warnMissingGameCdnUrl(lottery);
        return { gameCdnUrl, gameConfig };
    }

    if (type === GASHAPON || type === GARAPON) {
        const gameCdnUrl = applyAiCodeVersionSuffix(
            builtinLotteryGameCdnUrls[type],
            getBuiltinLotteryGameVersion(type)
        );
        if (!gameCdnUrl && warnOnMissing) warnMissingGameCdnUrl(lottery);
        return { gameCdnUrl, gameConfig };
    }

    return emptySource;
}
